import React from 'react';
import clsx from 'clsx';
import {
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_PLAN_FEATURES,
  PAYWALL_TESTIMONIALS,
  PAYWALL_TESTIMONIALS_PER_PAGE,
  PAYWALL_TRUST_BADGES,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallDivider,
  PaywallPdfPage,
  PaywallPriceText,
} from './components';
import Paywall3LegalText from './components/Paywall3LegalText';
import { ChevronIcon, CircleCheckIcon, StarIcon, StepCheckIcon } from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { useTestimonialsCarousel } from './useTestimonialsCarousel';
import { splitFileName } from './utils';

const TotalDueRow = ({ amountText }: { amountText: string }) => (
  <div className="flex w-full items-center justify-between gap-4 lg:whitespace-nowrap">
    <span className="text-[20px] font-semibold leading-normal text-[#292A33]">
      Total due today:
    </span>
    <PaywallPriceText
      value={amountText}
      className="shrink-0 whitespace-nowrap leading-normal text-[#292A33]"
    />
  </div>
);

const Step = ({
  state,
  label,
  index,
}: {
  state: 'complete' | 'active' | 'inactive';
  label: string;
  index?: number;
}) => (
  <div className="flex items-start gap-3">
    {state === 'complete' ? (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#292A33] text-white">
        <StepCheckIcon className="h-4 w-4" aria-hidden />
      </span>
    ) : (
      <span
        className={clsx(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[17px] leading-none',
          state === 'active'
            ? 'bg-[#4B7EFF] text-white'
            : 'bg-[#EEEEF2] text-[#787887]'
        )}
      >
        {index}
      </span>
    )}
    <span
      className={clsx(
        'whitespace-nowrap text-[17px] leading-normal',
        state === 'complete' && 'text-[#292A33]',
        state === 'active' && 'text-[#4B7EFF]',
        state === 'inactive' && 'text-[#878787]'
      )}
    >
      {label}
    </span>
  </div>
);

const StepLine = () => <span className="h-px w-8 shrink-0 bg-[#BFBFBF]" />;

const DocumentPreview = ({
  file,
  extension,
}: {
  file?: File | null;
  extension: string;
}) => (
  <div className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-[8px] bg-[#E5E6EB]">
    <div className="h-20 w-[57px] overflow-hidden bg-[#EBEBEB] shadow-[0px_4.042px_4.042px_0px_rgba(0,0,0,0.16)]">
      {file?.type === 'application/pdf' && (
        <PaywallPdfPage
          file={file}
          width={57}
          fallback={<span className="block h-full w-full" />}
        />
      )}
    </div>
    <span
      className={clsx(
        'pointer-events-none absolute left-[11px] top-3 flex h-[19px] items-center rounded-[6px] border-2 border-white px-1 text-[10px] font-medium uppercase leading-none text-white',
        PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400'
      )}
    >
      {extension}
    </span>
  </div>
);

const DocumentReadyCard = ({
  documentName,
  documentExtension,
  documentFile,
}: PaywallDocumentProps) => {
  const { base, suffix } = splitFileName(documentName, documentExtension);

  return (
    <div className="flex w-full items-center justify-between rounded-[16px] bg-[#DAF6ED] py-3 pl-6 pr-3">
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 text-[#292A33]">
        <span className="whitespace-nowrap text-[18px] font-semibold leading-5">
          Your document is ready!
        </span>
        <p
          title={documentName}
          className="flex w-full text-[14px] font-medium leading-5"
        >
          <span className="min-w-0 truncate">{base}</span>
          <span className="shrink-0 text-[#4B7EFF]">{suffix}</span>
        </p>
      </div>
      <DocumentPreview file={documentFile} extension={documentExtension} />
    </div>
  );
};

const Testimonials = () => {
  const pageCount = Math.ceil(
    PAYWALL_TESTIMONIALS.length / PAYWALL_TESTIMONIALS_PER_PAGE
  );
  const { trackRef, page, goToPage, handleScroll } =
    useTestimonialsCarousel(pageCount);

  return (
    <div className="flex w-full flex-col items-center gap-4 lg:gap-8">
      <p className="w-full text-center text-[28px] font-medium leading-normal text-[#292A33] lg:text-[36px]">
        What our users say
      </p>

      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="flex w-full snap-x snap-mandatory items-stretch gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {PAYWALL_TESTIMONIALS.map(({ name, comment, date }) => (
          <div
            key={name}
            className="flex w-64 shrink-0 snap-start flex-col gap-4 break-words rounded-[24px] border border-[rgba(0,161,112,0.24)] bg-white p-6 drop-shadow-[0px_8px_8px_rgba(0,161,112,0.1)] lg:w-[400px] lg:p-8"
          >
            <p className="text-[17px] font-semibold leading-normal text-[#292A33]">
              {name}
            </p>
            <div className="flex items-start gap-0.5">
              {Array.from({ length: 5 }).map((_, index) => (
                <span
                  key={index}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] bg-[#00A170]"
                >
                  <StarIcon className="h-4 w-4 text-white" aria-hidden />
                </span>
              ))}
            </div>
            <p className="text-[17px] leading-normal text-[#292A33]">
              {comment}
            </p>
            <p className="text-[17px] leading-normal text-[rgba(109,109,109,0.84)]">
              {date}
            </p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label="Previous reviews"
          disabled={page === 0}
          onClick={() => goToPage(page - 1)}
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-[8px]',
            page === 0
              ? 'bg-[#EEEEF2] text-[#CECFD4]'
              : 'bg-[#E5E6EB] text-[#292A33]'
          )}
        >
          <ChevronIcon className="h-6 w-6 -scale-x-100" aria-hidden />
        </button>
        <div className="flex items-center gap-2">
          {Array.from({ length: pageCount }).map((_, index) => (
            <span
              key={index}
              className={clsx(
                'h-1 w-6 rounded-[4px]',
                index === page ? 'bg-[#292A33]' : 'bg-[#CBCCD7]'
              )}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="Next reviews"
          disabled={page === pageCount - 1}
          onClick={() => goToPage(page + 1)}
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-[8px]',
            page === pageCount - 1
              ? 'bg-[#EEEEF2] text-[#CECFD4]'
              : 'bg-[#E5E6EB] text-[#292A33]'
          )}
        >
          <ChevronIcon className="h-6 w-6" aria-hidden />
        </button>
      </div>
    </div>
  );
};

const FullScreenPaywall3Layout = ({
  isOnlyPayment,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  recurringAdjective,
  renewalPriceText,
  documentName,
  documentExtension,
  documentFile,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    isOnlyPayment: boolean;
    hasTrial: boolean;
    trialDurationText: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    recurringPeriodText: string;
    recurringAdjective: string;
    renewalPriceText: string;
  }) => {
  const trialSegment = hasTrial && trialDurationText ? trialDurationText : '';
  const includesTitle = trialSegment
    ? `Your ${trialSegment} trial includes:`
    : 'Your subscription includes:';
  const renewalFeature = trialSegment
    ? `After the ${trialSegment} trial, your plan renews at ${renewalPriceText}`
    : `Your plan renews at ${renewalPriceText}`;

  return (
    <div className="flex w-full shrink-0 grow flex-col bg-[#F6F7F8] font-barlow text-[#292A33]">
      <div className="w-full px-4 lg:px-8">
        <div className="mx-auto flex w-full max-w-[1140px] items-center justify-between py-5">
          <img
            src="/images/logo/brand.svg"
            alt="PDF Genius"
            className="h-8 w-[136px] shrink-0"
          />
          {!isOnlyPayment && (
            <div className="hidden items-center gap-3 lg:flex">
              <Step state="complete" label="File is ready" />
              <StepLine />
              <Step state="active" index={2} label="Payment details" />
              <StepLine />
              <Step state="inactive" index={3} label="Download" />
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 pb-6 lg:px-8 lg:pb-10 lg:pt-4">
        <div className="mx-auto flex w-full max-w-[1140px] flex-col gap-6 lg:gap-10">
          <div className="grid w-full grid-cols-1 items-start gap-4 lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:gap-x-6 lg:gap-y-4">
            <section className="order-2 flex w-full min-w-0 flex-col gap-1 rounded-[12px] bg-white p-4 lg:order-none lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:px-8 lg:py-6">
              <div className="flex w-full flex-col gap-2 lg:py-2">
                <TotalDueRow amountText={dueTodayAmountText} />
                <PaywallDivider />
              </div>

              {checkoutMethod && (
                <div className="relative w-full">
                  <PaywallCheckoutFrame
                    checkoutContainerRef={checkoutContainerRef}
                    paymentStatus={paymentStatus}
                  />
                </div>
              )}

              <div className="flex w-full flex-col gap-4 pt-2">
                <Paywall3LegalText
                  trialSegment={trialSegment}
                  dueTodayAmountText={dueTodayAmountText}
                  recurringAmountText={recurringAmountText}
                  recurringPeriodText={recurringPeriodText}
                  recurringAdjective={recurringAdjective}
                />
                <PaywallDivider />
                <div className="flex w-full flex-wrap items-start gap-2">
                  {PAYWALL_TRUST_BADGES.map(({ label, Icon }) => (
                    <span
                      key={label}
                      className="flex shrink-0 items-center justify-center gap-1 rounded-[6px] bg-[#DAF6ED] px-1.5 py-1"
                    >
                      <Icon
                        className="h-4 w-4 shrink-0 text-[#00A170]"
                        aria-hidden
                      />
                      <span className="whitespace-nowrap text-[11px] font-semibold uppercase leading-normal text-[#009E5E]">
                        {label}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </section>

            {!isOnlyPayment && (
              <div className="order-1 w-full min-w-0 lg:order-none lg:col-start-2">
                <DocumentReadyCard
                  documentName={documentName}
                  documentExtension={documentExtension}
                  documentFile={documentFile}
                />
              </div>
            )}

            <div className="order-3 flex w-full min-w-0 flex-col gap-4 rounded-[12px] bg-white px-4 py-6 lg:order-none lg:col-start-2 lg:px-8">
              <div className="flex w-full flex-col gap-4 lg:gap-6">
                <p className="text-[18px] font-bold leading-normal text-[#292A33]">
                  {includesTitle}
                </p>
                <ul className="flex w-full flex-col gap-2.5">
                  {PAYWALL_PLAN_FEATURES.map((feature) => (
                    <li
                      className="flex w-full items-center gap-2"
                      key={feature}
                    >
                      <CircleCheckIcon
                        className="h-6 w-6 shrink-0 text-[#016FD0]"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 break-words text-[18px] leading-normal text-[#292A33]">
                        {feature}
                      </span>
                    </li>
                  ))}
                  <li className="flex w-full items-center gap-2">
                    <CircleCheckIcon
                      className="h-6 w-6 shrink-0 text-[#016FD0]"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 break-words text-[18px] font-medium leading-normal text-[#292A33]">
                      {renewalFeature}
                    </span>
                  </li>
                </ul>
              </div>
              <PaywallDivider />
              <TotalDueRow amountText={dueTodayAmountText} />
            </div>
          </div>

          <Testimonials />
        </div>
      </div>
    </div>
  );
};

export default FullScreenPaywall3Layout;
